import { AlertTriangle, Check, Copy, Loader2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { statusLabel, statusTone } from '../lib/format.js';
import { Button } from './ui/button.js';
import { CardContent, CardDescription, CardHeader, CardShell, CardTitle } from './ui/card.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip.js';

/**
 * Primitivos compartilhados das telas.
 *
 * A API destes componentes é a que as telas já usam; a implementação passou para os
 * primitivos novos (`./ui/*`) para a migração acontecer tela a tela, sem quebrar nada.
 */

export function Card({
  title,
  description,
  actions,
  children,
  bodyless,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Tables provide their own padding, so the body wrapper is skipped for them. */
  bodyless?: boolean;
  className?: string;
}) {
  return (
    <CardShell as={title ? 'section' : 'div'} className={cn('overflow-hidden', className)}>
      {(title || actions) && (
        <CardHeader className="items-center">
          <div className="min-w-0">
            {title && <CardTitle>{title}</CardTitle>}
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </CardHeader>
      )}
      {bodyless ? children : <CardContent>{children}</CardContent>}
    </CardShell>
  );
}

const STAT_TONE: Record<string, string> = {
  ok: 'text-success',
  warn: 'text-warning',
  danger: 'text-danger',
};

export function Stat({
  label,
  value,
  hint,
  tone,
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'ok' | 'warn' | 'danger';
  icon?: ReactNode;
}) {
  return (
    <CardShell className="flex items-center gap-4 px-4 py-3.5">
      {icon && (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary [&_svg]:size-5">
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div
          className={cn(
            'mt-0.5 text-[26px] leading-tight font-semibold tracking-tight tabular-nums',
            tone && STAT_TONE[tone],
          )}
        >
          {value}
        </div>
        {hint && <div className="mt-0.5 text-xs text-subtle">{hint}</div>}
      </div>
    </CardShell>
  );
}

export function StatusPill({ status, label }: { status: string; label?: string }) {
  return (
    <span className={`pill pill-${statusTone(status)}`}>
      <span className="dot" aria-hidden="true" />
      {label ?? statusLabel(status)}
    </span>
  );
}

export function Meter({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, percent));
  const tone = clamped >= 100 ? 'danger' : clamped >= 80 ? 'warn' : '';
  return (
    <div
      className="meter"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={`meter-fill ${tone}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

export function Empty({
  title,
  children,
  icon,
}: {
  title: string;
  children?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 px-4 py-9 text-center text-muted-foreground">
      {icon && (
        <span className="mb-1 flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground [&_svg]:size-5">
          {icon}
        </span>
      )}
      <h3 className="m-0 text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  children: ReactNode;
}) {
  return <div className={`banner banner-${tone}`}>{children}</div>;
}

export function Spinner() {
  return (
    <Loader2 className="size-4 animate-spin text-primary" role="status" aria-label="Carregando" />
  );
}

export function Loading({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-9 text-sm text-muted-foreground">
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

/**
 * Error state.
 *
 * Always surfaces the request id: it is the single thing that turns "deu erro" into a
 * traceable event in the Log Explorer (PRD §33, §35, §120).
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const apiError = error as { code?: string; message?: string; requestId?: string | null };
  return (
    <Empty title="Não foi possível carregar" icon={<AlertTriangle />}>
      <p className="muted m-0">{apiError?.message ?? 'Erro inesperado.'}</p>
      {apiError?.requestId && (
        <p className="subtle mono m-0">
          Request ID: <code>{apiError.requestId}</code>
        </p>
      )}
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
          Tentar novamente
        </Button>
      )}
    </Empty>
  );
}

/**
 * Botão de copiar com confirmação visual (check por 1,5 s). O `label` diz O QUE se copia
 * e vira o nome acessível — o texto visível é só o ícone.
 */
export function CopyButton({
  value,
  label = 'Copiar',
  size = 'icon-sm',
  variant = 'outline',
  className,
}: {
  value: string;
  label?: string;
  size?: 'icon' | 'icon-sm' | 'icon-xs';
  variant?: 'outline' | 'ghost';
  className?: string;
}) {
  const [done, setDone] = useState(false);
  // Provider local: o botão aparece em telas renderizadas fora do shell (e em testes).
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className={className}
            aria-label={label}
            onClick={() => {
              void navigator.clipboard?.writeText(value).then(() => {
                setDone(true);
                setTimeout(() => setDone(false), 1500);
              });
            }}
          >
            {done ? <Check className="text-success" /> : <Copy />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{done ? 'Copiado!' : label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
